import axios, { AxiosInstance, AxiosError } from 'axios';
import { getAccessToken, logout } from './centralAuth';
import { fetchSwitchContext } from './centralBootstrap';
import { store } from '../store';
import { bootWorkspaceClosed } from '../store/slices/bootSlice';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and workspace ID
    this.client.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Send selected workspace so backend scopes data correctly,
      // even if the JWT still contains the original workspace from login.
      const workspaceId = store.getState().boot.context?.currentWorkspace?.id;
      if (workspaceId) {
        config.headers['X-Workspace-Id'] = workspaceId;
      }

      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired or invalid - clear and redirect to login
          logout();
          window.location.href = '/';
        }

        // 409 WORKSPACE_CLOSED — re-fetch switch-context for guaranteed otherWorkspaces
        if (error.response?.status === 409) {
          const data = error.response.data as Record<string, unknown> | undefined;
          if (data?.errorCode === 'WORKSPACE_CLOSED') {
            const workspaceName =
              (data?.workspaceName as string) ?? 'your workspace';

            // Re-fetch switch-context to get fresh otherWorkspaces from Central
            const switchResult = await fetchSwitchContext();
            const otherWorkspaces =
              switchResult.type === 'workspace_closed'
                ? switchResult.otherWorkspaces
                : switchResult.type === 'success'
                  ? switchResult.data.otherWorkspaces
                  : [];

            store.dispatch(
              bootWorkspaceClosed({ workspaceName, otherWorkspaces })
            );
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Recommendations
  async getRecommendations(params?: { status?: string; agentType?: string }) {
    const response = await this.client.get('/recommendations', { params });
    return response.data;
  }

  async getRecommendation(id: string) {
    const response = await this.client.get(`/recommendations/${id}`);
    return response.data;
  }

  async updateRecommendationStatus(id: string, status: string, reason?: string) {
    const response = await this.client.patch(`/recommendations/${id}/status`, {
      status,
      reason,
    });
    return response.data;
  }

  // Alerts
  async getAlerts(params?: { status?: string; priority?: string }) {
    const response = await this.client.get('/alerts', { params });
    return response.data;
  }

  async getUnreadAlertCount() {
    const response = await this.client.get('/alerts/unread-count');
    return response.data;
  }

  async acknowledgeAlert(id: string) {
    const response = await this.client.patch(`/alerts/${id}/acknowledge`);
    return response.data;
  }

  async dismissAlert(id: string) {
    const response = await this.client.patch(`/alerts/${id}/dismiss`);
    return response.data;
  }

  // Agents
  async getAgents() {
    const response = await this.client.get('/agents');
    return response.data;
  }

  async runAgent(agentType: string) {
    const response = await this.client.post(`/agents/${agentType}/run`);
    return response.data;
  }

  async getAgentRuns() {
    const response = await this.client.get('/agents/runs');
    return response.data;
  }

  async getAgentRun(runId: string) {
    const response = await this.client.get(`/agents/runs/${runId}`);
    return response.data;
  }

  // Chat
  async getChatSessions() {
    const response = await this.client.get('/chat/sessions');
    return response.data;
  }

  async createChatSession(data?: { title?: string; contextType?: string; contextId?: string }) {
    const response = await this.client.post('/chat/sessions', data);
    return response.data;
  }

  async getChatMessages(sessionId: string) {
    const response = await this.client.get(`/chat/sessions/${sessionId}/messages`);
    return response.data;
  }

  async sendChatMessage(sessionId: string, content: string) {
    const response = await this.client.post(`/chat/sessions/${sessionId}/messages`, {
      content,
    });
    return response.data;
  }

  async deleteChatSession(sessionId: string) {
    await this.client.delete(`/chat/sessions/${sessionId}`);
  }

  // Snapshots
  async getUnifiedSnapshot() {
    const response = await this.client.get('/snapshots/unified');
    return response.data;
  }

  async refreshSnapshots(sources?: string[]) {
    const response = await this.client.post('/snapshots/refresh', { sources });
    return response.data;
  }

  // Plans
  async getPlans(params?: { status?: string }) {
    const response = await this.client.get('/plans', { params });
    return response.data;
  }

  async getPlan(id: string) {
    const response = await this.client.get(`/plans/${id}`);
    return response.data;
  }

  async createPlan(data: { title: string; goal?: string; recommendationIds?: string[] }) {
    const response = await this.client.post('/plans', data);
    return response.data;
  }

  async updatePlanStatus(planId: string, status: string) {
    const response = await this.client.patch(`/plans/${planId}/status`, { status });
    return response.data;
  }

  async updatePlanStepStatus(planId: string, stepId: string, status: string, notes?: string) {
    const response = await this.client.patch(`/plans/${planId}/steps/${stepId}/status`, {
      status,
      notes,
    });
    return response.data;
  }

  // Settings
  async getSettingsStatus() {
    const response = await this.client.get('/settings/status');
    return response.data;
  }

  async getSettingsPreferences() {
    const response = await this.client.get('/settings/preferences');
    return response.data;
  }

  async updateSettingsPreferences(notifications: Partial<{
    alertsEmail: boolean;
    alertsInApp: boolean;
    recommendationsEmail: boolean;
    recommendationsInApp: boolean;
  }>) {
    const response = await this.client.patch('/settings/preferences', { notifications });
    return response.data;
  }
  // Documents
  async uploadDocument(file: File, documentType: string, taxYear?: number, title?: string, replaceDocumentId?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (taxYear) formData.append('taxYear', String(taxYear));
    if (title) formData.append('title', title);
    if (replaceDocumentId) formData.append('replaceDocumentId', replaceDocumentId);
    const response = await this.client.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getDocuments() {
    const response = await this.client.get('/documents');
    return response.data;
  }

  async getDocument(id: string) {
    const response = await this.client.get(`/documents/${id}`);
    return response.data;
  }

  async deleteDocument(id: string) {
    await this.client.delete(`/documents/${id}`);
  }

  async retryDocument(id: string) {
    const response = await this.client.post(`/documents/${id}/retry`);
    return response.data;
  }

  // Profile
  async getTaxProfile() {
    const response = await this.client.get('/profile/tax');
    return response.data;
  }

  async getInsuranceSummary() {
    const response = await this.client.get('/profile/insurance');
    return response.data;
  }

  async getEstateSummary() {
    const response = await this.client.get('/profile/estate');
    return response.data;
  }

  async getProfileCompleteness() {
    const response = await this.client.get('/profile/completeness');
    return response.data;
  }

  async getNextActions() {
    const response = await this.client.get('/profile/next-actions');
    return response.data;
  }

  async getBeneficiaryReview() {
    const response = await this.client.get('/profile/beneficiaries/review');
    return response.data;
  }

  async getUnresolvedBeneficiaries() {
    const response = await this.client.get('/profile/beneficiaries/unresolved');
    return response.data;
  }

  async linkBeneficiary(beneficiaryId: string, memberId: string) {
    const response = await this.client.patch(`/profile/beneficiaries/${beneficiaryId}/link`, { memberId });
    return response.data;
  }

  async unlinkBeneficiary(beneficiaryId: string) {
    const response = await this.client.patch(`/profile/beneficiaries/${beneficiaryId}/unlink`);
    return response.data;
  }

  async updateBeneficiaryPriority(beneficiaryId: string, priorityType: string) {
    const response = await this.client.patch(`/profile/beneficiaries/${beneficiaryId}/priority`, { priorityType });
    return response.data;
  }

  async createManualBeneficiary(data: {
    beneficiaryDisplayName: string;
    priorityType?: string;
    beneficiaryMemberId?: string;
    accountId?: string;
    relatedEstateDocumentId?: string;
    sourceDocumentId?: string;
    percentage?: number;
    notes?: string;
  }) {
    const response = await this.client.post('/profile/beneficiaries/manual', data);
    return response.data;
  }

  async updateManualBeneficiary(beneficiaryId: string, data: {
    beneficiaryDisplayName?: string;
    priorityType?: string;
    beneficiaryMemberId?: string;
    percentage?: number;
    notes?: string;
    accountId?: string | null;
    relatedEstateDocumentId?: string | null;
    sourceDocumentId?: string | null;
  }) {
    const response = await this.client.patch(`/profile/beneficiaries/${beneficiaryId}/manual`, data);
    return response.data;
  }

  async deleteManualBeneficiary(beneficiaryId: string) {
    const response = await this.client.delete(`/profile/beneficiaries/${beneficiaryId}/manual`);
    return response.data;
  }

  async verifyFact(factId: string, body: { action: string; correctedValue?: unknown }) {
    const response = await this.client.patch(`/profile/facts/${factId}/verify`, body);
    return response.data;
  }

  async addHouseholdMember(data: { name: string; relationship: string; isDependent?: boolean }) {
    const response = await this.client.post('/profile/members', data);
    return response.data;
  }

  async addIncomeSource(data: { memberId: string; incomeType: string; annualAmount: number; taxYear: number; payerName?: string }) {
    const response = await this.client.post('/profile/income', data);
    return response.data;
  }
}

export const api = new ApiClient();
